<template>
    <v-dialog
        :model-value="isOpen"
        eager
        max-width="800"
        scrollable
        transition="slide-y-transition"
        @after-leave="onAfterLeave"
        @click:outside="closeDialog"
        @keydown.esc="closeDialog"
    >
        <v-card>
            <v-card-title class="text-subtitle-1">
                {{ t('common.dashboard.dialogs.notSentOrdersTitle') }} {{ dateLabel }}:
                <strong
                    >{{ partner }}<span v-if="currentStep === 2"> / {{ product }}</span></strong
                >
            </v-card-title>

            <v-card-text class="pa-0">
                <v-window v-model="currentStep">
                    <v-window-item :value="1">
                        <v-data-table
                            :headers="headers"
                            :items="ordersProductsPerDayNotSent"
                            class="fixed-header-table"
                            density="compact"
                            fixed-header
                            item-value="prodName"
                        >
                            <template #item.quantity="{ item }">{{ formatNumber(item.quantity) }}</template>
                            <template #item.details="{ item }">
                                <v-tooltip :text="t('common.dashboard.tooltips.showDetails')" location="top">
                                    <template #activator="{ props: tooltipProps }">
                                        <v-btn
                                            v-bind="tooltipProps"
                                            color="blue"
                                            icon="mdi-ballot"
                                            size="small"
                                            variant="text"
                                            @click="getOrderList(item)"
                                        />
                                    </template>
                                </v-tooltip>
                            </template>
                        </v-data-table>
                    </v-window-item>

                    <v-window-item :value="2">
                        <v-card-actions>
                            <v-btn color="primary" prepend-icon="mdi-arrow-left" variant="text" @click="currentStep = 1">
                                {{ t('common.dashboard.buttons.backToProducts') }}
                            </v-btn>
                        </v-card-actions>
                        <v-data-table
                            :headers="ordersHeaders"
                            :items="ordersList"
                            class="fixed-header-table"
                            density="compact"
                            fixed-header
                            :item-value="orderItemValue"
                        >
                            <template #item.quantity="{ item }">{{ formatNumber(item.quantity) }}</template>
                        </v-data-table>
                    </v-window-item>
                </v-window>
            </v-card-text>

            <v-card-actions>
                <v-btn color="error" variant="text" @click="closeDialog">{{ t('common.dashboard.buttons.close') }}</v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script setup lang="ts">
/*
Organization / ordering applied:
1. Imports
2. Props / Emits
3. Constants
4. Stores & top-level services
5. Reactive state (refs/reactive)
6. Validation rules & setup
7. Computed
8. Handlers / methods
9. Helpers / lifecycle
*/

// 1. Imports
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'

import { errorHandler } from '@/composables/errorHandler'
import { useDashboardStore } from '@/stores'
import type { OfflineOrderNotSentDetailRow } from '@/stores/dashboard'
import type { DataTableHeader } from '@/types/dataTable'
import { formatDateForDisplay, formatNumber } from '@/views/Dashboard/dashboardUtils'

type ProductRow = {
    dueDate?: string
    partner?: string
    productId?: string | number
    prodName?: string
    toSend?: unknown
    quantity?: unknown
}

type LegacyOrderNotSentDetailRow = {
    orderNumber?: string
    customerNumber?: string
    quantity?: unknown
}

type OrderNotSentDetailRow = LegacyOrderNotSentDetailRow | OfflineOrderNotSentDetailRow

// 2. Props / Emits
const props = withDefaults(
    defineProps<{
        isOpen?: boolean
        partner?: string
        date?: Date | null
        ordersProductsPerDayNotSent?: ProductRow[]
    }>(),
    { isOpen: false, partner: '', date: null, ordersProductsPerDayNotSent: () => [] }
)

const emit = defineEmits<{
    'after-leave-not-sent-orders-products-dialog': []
    'close-not-sent-orders-products-dialog': []
}>()

// 3. Constants
const OFFLINE_PARTNER = 'Offline'

// 4. Stores & top-level services
const dashboardStore = useDashboardStore()
const { t } = useI18n()

// 5. Reactive state (refs/reactive)
const currentStep = ref(1)
const ordersList = ref<OrderNotSentDetailRow[]>([])
const product = ref('')

// 7. Computed
const dateLabel = computed(() => formatDateForDisplay(props.date || new Date()))
const isOffline = computed(() => props.partner === OFFLINE_PARTNER)
const headers = computed<DataTableHeader[]>(() => [
    { title: t('common.dashboard.tableHeaders.product'), key: 'prodName', sortable: false },
    { title: t('common.dashboard.tableHeaders.toSend'), key: 'toSend', sortable: false, align: 'center' },
    { title: t('common.dashboard.tableHeaders.quantity'), key: 'quantity', sortable: false, align: 'center' },
    { title: t('common.dashboard.tableHeaders.details'), key: 'details', sortable: false, align: 'center' }
])
const ordersHeaders = computed<DataTableHeader[]>(() => {
    if (isOffline.value) {
        return [
            { title: t('common.dashboard.tableHeaders.jobNumber'), key: 'jobNumber', sortable: false },
            { title: t('common.dashboard.tableHeaders.customer'), key: 'customerName', sortable: false },
            { title: t('common.dashboard.tableHeaders.quantity'), key: 'quantity', sortable: false }
        ]
    }

    return [
        { title: t('common.dashboard.tableHeaders.order'), key: 'orderNumber', sortable: false },
        { title: t('common.dashboard.tableHeaders.customerNumber'), key: 'customerNumber', sortable: false },
        { title: t('common.dashboard.tableHeaders.quantity'), key: 'quantity', sortable: false }
    ]
})
const orderItemValue = computed(() => (isOffline.value ? 'jobNumber' : 'orderNumber'))

// 8. Handlers / methods
async function getOrderList(item: ProductRow): Promise<void> {
    try {
        const partner = item.partner || props.partner
        const queryParams = new URLSearchParams({
            sendDate: item.dueDate || '',
            productId: String(item.productId || '')
        })

        const response =
            partner === OFFLINE_PARTNER
                ? await dashboardStore.getOfflineOrdersByProductType({ query: queryParams.toString() })
                : await dashboardStore.getOrdersProductsPerDayNotSentByOrder({
                      query: `${queryParams.toString()}&partner=${encodeURIComponent(partner)}`
                  })

        ordersList.value = (response?.data?.results || []) as OrderNotSentDetailRow[]
        product.value = item.prodName || ''
        currentStep.value = 2
    } catch (error) {
        errorHandler(error)
    }
}

function closeDialog(): void {
    emit('close-not-sent-orders-products-dialog')
}

// 9. Helpers / lifecycle
function onAfterLeave(): void {
    currentStep.value = 1
    ordersList.value = []
    product.value = ''
    emit('after-leave-not-sent-orders-products-dialog')
}
</script>
